import { type QueryClient, useSuspenseQuery } from "@tanstack/react-query";
import {
	createRootRouteWithContext,
	createRoute,
	createRouter,
	lazyRouteComponent,
	Outlet,
	redirect,
	useParams,
} from "@tanstack/react-router";
import { FeedbackButton } from "./components/feedback-button.tsx";
import { Page } from "./components/ui.tsx";
import { addressStatusQuery } from "./lib/address-status.ts";
import { type Me, meQuery } from "./lib/api.ts";
import { candidateListQuery, clientListQuery, registrationAddressQuery } from "./lib/queries.ts";
// The staff shell and Home come in the first download (no extra round trip on a slow
// phone); every other screen loads on demand. Candidate and client links are a
// separate small app (link.html), so none of this reaches them.
import {
	Account as AccountPage,
	ChooseAgency as ChooseAgencyPage,
	Home as HomePage,
	Upload as UploadPage,
} from "./routes/app.tsx";
import { Crash } from "./routes/crash.tsx";
import { NotFound } from "./routes/not-found.tsx";
import { StaffLayout } from "./routes/staff-layout.tsx";

// The feedback button sits above every route, so it's still there when a screen breaks.
const root = createRootRouteWithContext<{ queryClient: QueryClient }>()({
	component: () => (
		<>
			<Outlet />
			<FeedbackButton />
		</>
	),
});

const signIn = createRoute({
	getParentRoute: () => root,
	path: "/sign-in",
	component: lazyRouteComponent(() => import("./routes/auth.tsx"), "SignIn"),
});

const signInLink = createRoute({
	getParentRoute: () => root,
	path: "/sign-in/link",
	component: lazyRouteComponent(() => import("./routes/auth.tsx"), "SignInLink"),
});

const newEmail = createRoute({
	getParentRoute: () => root,
	path: "/sign-in/new-email",
	component: lazyRouteComponent(() => import("./routes/auth.tsx"), "NewEmailLink"),
});

async function requireUser(queryClient: QueryClient): Promise<Me> {
	// fetchQuery (not ensureQueryData) so a sign-in that invalidated "me" is refetched here.
	const me = await queryClient.fetchQuery(meQuery);
	if (!me) throw redirect({ to: "/sign-in" });
	return me;
}

/** Signed in. */
const staff = createRoute({
	getParentRoute: () => root,
	id: "staff",
	beforeLoad: async ({ context }) => {
		await requireUser(context.queryClient);
	},
	component: StaffLayout,
});

function useMe(): Me {
	return useSuspenseQuery(meQuery).data as Me;
}

const AnswerSsgPage = lazyRouteComponent(() => import("./routes/answer-ssg.tsx"), "AnswerSsg");
const platformModule = () => import("./routes/platform.tsx");
const PlatformAgenciesPage = lazyRouteComponent(platformModule, "PlatformAgencies");
const platform = createRoute({
	getParentRoute: () => staff,
	path: "/platform",
	component: () => <PlatformAgenciesPage me={useMe()} />,
});

const platformFeedback = createRoute({
	getParentRoute: () => staff,
	path: "/platform/feedback",
	validateSearch: (search: Record<string, unknown>): { id?: string } =>
		typeof search.id === "string" && /^[0-9a-f-]{36}$/.test(search.id) ? { id: search.id } : {},
	component: lazyRouteComponent(() => import("./routes/platform-feedback.tsx"), "PlatformFeedback"),
});

const answerSsg = createRoute({
	getParentRoute: () => staff,
	path: "/answer-ssg/$agencyId",
	validateSearch: (search: Record<string, unknown>): { agency?: string } =>
		typeof search.agency === "string" && search.agency ? { agency: search.agency.slice(0, 120) } : {},
	component: function AnswerSsgRoute() {
		const { agencyId } = answerSsg.useParams();
		const { agency } = answerSsg.useSearch();
		return <AnswerSsgPage me={useMe()} agencyId={agencyId} {...(agency ? { agencyName: agency } : {})} />;
	},
});

const chooseAgency = createRoute({
	getParentRoute: () => staff,
	path: "/choose-agency",
	component: () => <ChooseAgencyPage me={useMe()} />,
});

/** Signed in, two-factor on, agency chosen. */
const tenant = createRoute({
	getParentRoute: () => staff,
	id: "tenant",
	beforeLoad: async ({ context }) => {
		const me = await requireUser(context.queryClient);
		if (!me.activeAgencyId) throw redirect({ to: "/choose-agency" });
	},
	component: Outlet,
});

const home = createRoute({ getParentRoute: () => tenant, path: "/", component: () => <HomePage me={useMe()} /> });
const upload = createRoute({ getParentRoute: () => tenant, path: "/upload", component: UploadPage });
const account = createRoute({
	getParentRoute: () => staff,
	path: "/account",
	component: () => <AccountPage me={useMe()} />,
});

// Each section loads its own code on first visit, so a cheap phone only downloads what it shows.
const candidatesModule = () => import("./routes/candidates.tsx");
const CandidateList = lazyRouteComponent(candidatesModule, "CandidateList");
const CandidateRecordPage = lazyRouteComponent(candidatesModule, "CandidateRecord");
const CandidateFormPage = lazyRouteComponent(candidatesModule, "CandidateForm");

const candidateList = createRoute({
	getParentRoute: () => tenant,
	path: "/candidates",
	validateSearch: (search: Record<string, unknown>): { q?: string } =>
		typeof search.q === "string" && search.q ? { q: search.q.slice(0, 100) } : {},
	// Start fetching the first page alongside the screen's code, not after it.
	loaderDeps: ({ search }) => ({ q: search.q ?? "" }),
	loader: ({ context, deps }) => void context.queryClient.prefetchInfiniteQuery(candidateListQuery(deps.q, "")),
	component: CandidateList,
});
// The boxes that depend on these arrive with the form, not after it, so nothing
// jumps while someone is typing. A failure just leaves them out.
// Never waits more than 1.5s: on a very slow line the form shows without them.
const formExtras = (extras: Promise<unknown>[]) =>
	Promise.race([Promise.allSettled(extras), new Promise((r) => setTimeout(r, 1500))]).then(() => undefined);
/** After a second, the page's title and "Loading" rather than a blank screen. */
const loadingForm = (title: string) => () => (
	<Page section="Candidates" title={title}>
		<p>Loading</p>
	</Page>
);

const candidateNew = createRoute({
	getParentRoute: () => tenant,
	path: "/candidates/new",
	loader: ({ context: { queryClient } }) =>
		formExtras([
			queryClient.ensureQueryData(addressStatusQuery),
			queryClient.ensureQueryData(registrationAddressQuery),
		]),
	component: () => <CandidateFormPage />,
});
const candidateRecord = createRoute({
	getParentRoute: () => tenant,
	path: "/candidates/$id",
	component: function CandidateRoute() {
		const { id } = useParams({ from: "/staff/tenant/candidates/$id" });
		return <CandidateRecordPage id={id} />;
	},
});
const candidateEdit = createRoute({
	getParentRoute: () => tenant,
	path: "/candidates/$id/edit",
	loader: ({ context: { queryClient } }) => formExtras([queryClient.ensureQueryData(addressStatusQuery)]),
	pendingComponent: loadingForm("Edit candidate"),
	component: function CandidateEditRoute() {
		const { id } = useParams({ from: "/staff/tenant/candidates/$id/edit" });
		return <CandidateFormPage id={id} />;
	},
});

const clientsModule = () => import("./routes/clients.tsx");
const ClientListPage = lazyRouteComponent(clientsModule, "ClientList");
const ClientRecordPage = lazyRouteComponent(clientsModule, "ClientRecord");
const ClientFormPage = lazyRouteComponent(clientsModule, "ClientForm");

const clientList = createRoute({
	getParentRoute: () => tenant,
	path: "/clients",
	loader: ({ context }) => void context.queryClient.prefetchInfiniteQuery(clientListQuery("", "")),
	component: ClientListPage,
});
const clientNew = createRoute({
	getParentRoute: () => tenant,
	path: "/clients/new",
	component: () => <ClientFormPage />,
});
const clientRecord = createRoute({
	getParentRoute: () => tenant,
	path: "/clients/$id",
	component: function ClientRoute() {
		const { id } = useParams({ from: "/staff/tenant/clients/$id" });
		return <ClientRecordPage id={id} />;
	},
});
const clientEdit = createRoute({
	getParentRoute: () => tenant,
	path: "/clients/$id/edit",
	component: function ClientEditRoute() {
		const { id } = useParams({ from: "/staff/tenant/clients/$id/edit" });
		return <ClientFormPage id={id} />;
	},
});

const importsModule = () => import("./routes/imports.tsx");
const ImportStartPage = lazyRouteComponent(importsModule, "ImportStart");
const ImportJobPage = lazyRouteComponent(importsModule, "ImportJobPage");
const importStart = createRoute({ getParentRoute: () => tenant, path: "/imports", component: ImportStartPage });
const importJob = createRoute({
	getParentRoute: () => tenant,
	path: "/imports/$id",
	component: function ImportJobRoute() {
		const { id } = useParams({ from: "/staff/tenant/imports/$id" });
		return <ImportJobPage id={id} />;
	},
});

const complianceModule = () => import("./routes/compliance.tsx");
const checkQueue = createRoute({
	getParentRoute: () => tenant,
	path: "/compliance",
	component: lazyRouteComponent(complianceModule, "CheckQueue"),
});
const documentSettings = createRoute({
	getParentRoute: () => tenant,
	path: "/settings/documents",
	component: lazyRouteComponent(complianceModule, "DocumentSettings"),
});

const formsModule = () => import("./routes/forms.tsx");
const formsPage = createRoute({
	getParentRoute: () => tenant,
	path: "/settings/forms",
	component: lazyRouteComponent(formsModule, "FormsPage"),
});
const FormBuilderLazy = lazyRouteComponent(formsModule, "FormBuilder");
const formBuilder = createRoute({
	getParentRoute: () => tenant,
	path: "/settings/forms/$id",
	component: function FormBuilderRoute() {
		const { id } = useParams({ from: "/staff/tenant/settings/forms/$id" });
		return <FormBuilderLazy id={id} />;
	},
});

const agreementsPage = createRoute({
	getParentRoute: () => tenant,
	path: "/settings/agreements",
	component: lazyRouteComponent(() => import("./routes/agreements.tsx"), "AgreementsPage"),
});

const ratesModule = () => import("./routes/rates.tsx");
const rateCards = createRoute({
	getParentRoute: () => tenant,
	path: "/rates",
	component: lazyRouteComponent(ratesModule, "RateCards"),
});
const RateCardLazy = lazyRouteComponent(ratesModule, "RateCardPage");
const rateCard = createRoute({
	getParentRoute: () => tenant,
	path: "/rates/$id",
	component: function RateCardRoute() {
		const { id } = useParams({ from: "/staff/tenant/rates/$id" });
		return <RateCardLazy id={id} />;
	},
});

const reportsModule = () => import("./routes/reports.tsx");
const reportsPage = createRoute({
	getParentRoute: () => tenant,
	path: "/reports",
	component: lazyRouteComponent(reportsModule, "Reports"),
});
const wallBoard = createRoute({
	getParentRoute: () => tenant,
	path: "/board",
	component: lazyRouteComponent(reportsModule, "WallBoard"),
});

const teamPage = createRoute({
	getParentRoute: () => tenant,
	path: "/settings/team",
	component: lazyRouteComponent(() => import("./routes/team.tsx"), "TeamPage"),
});

const payrollPage = createRoute({
	getParentRoute: () => tenant,
	path: "/payroll",
	validateSearch: (search: Record<string, unknown>): { week?: string } =>
		typeof search.week === "string" && /^\d{4}-\d{2}-\d{2}$/.test(search.week) ? { week: search.week } : {},
	component: lazyRouteComponent(() => import("./routes/payroll.tsx"), "PayrollPage"),
});

const vacanciesModule = () => import("./routes/vacancies.tsx");
const vacanciesPage = createRoute({
	getParentRoute: () => tenant,
	path: "/vacancies",
	component: lazyRouteComponent(vacanciesModule, "Vacancies"),
});
const VacancyLazy = lazyRouteComponent(vacanciesModule, "VacancyPage");
const vacancyPage = createRoute({
	getParentRoute: () => tenant,
	path: "/vacancies/$id",
	component: function VacancyRoute() {
		const { id } = useParams({ from: "/staff/tenant/vacancies/$id" });
		return <VacancyLazy id={id} />;
	},
});

const TasksPageLazy = lazyRouteComponent(() => import("./routes/tasks.tsx"), "TasksPage");
const tasksPage = createRoute({
	getParentRoute: () => tenant,
	path: "/tasks",
	component: () => <TasksPageLazy me={useMe()} />,
});

const waysPage = createRoute({
	getParentRoute: () => tenant,
	path: "/settings/ways",
	component: lazyRouteComponent(() => import("./routes/ways.tsx"), "WaysPage"),
});

const accessLog = createRoute({
	getParentRoute: () => tenant,
	path: "/settings/access",
	component: lazyRouteComponent(() => import("./routes/access.tsx"), "AccessLog"),
});

const bookingsModule = () => import("./routes/bookings.tsx");
const BookingPageLazy = lazyRouteComponent(bookingsModule, "BookingPage");
const bookingList = createRoute({
	getParentRoute: () => tenant,
	path: "/bookings",
	component: lazyRouteComponent(bookingsModule, "BookingList"),
});
const bookingPlan = createRoute({
	getParentRoute: () => tenant,
	path: "/plan",
	validateSearch: (search: Record<string, unknown>): { week?: string } =>
		typeof search.week === "string" && /^\d{4}-\d{2}-\d{2}$/.test(search.week) ? { week: search.week } : {},
	component: lazyRouteComponent(bookingsModule, "BookingPlan"),
});
const bookingNew = createRoute({
	getParentRoute: () => tenant,
	path: "/bookings/new",
	component: lazyRouteComponent(bookingsModule, "NewBooking"),
});
const bookingPage = createRoute({
	getParentRoute: () => tenant,
	path: "/bookings/$id",
	component: function BookingRoute() {
		const { id } = useParams({ from: "/staff/tenant/bookings/$id" });
		return <BookingPageLazy id={id} />;
	},
});

const timesheetsPage = createRoute({
	getParentRoute: () => tenant,
	path: "/timesheets",
	validateSearch: (search: Record<string, unknown>): { week?: string; client?: string } => ({
		...(typeof search.week === "string" && /^\d{4}-\d{2}-\d{2}$/.test(search.week) ? { week: search.week } : {}),
		...(typeof search.client === "string" && /^[0-9a-f-]{36}$/.test(search.client) ? { client: search.client } : {}),
	}),
	component: lazyRouteComponent(() => import("./routes/timesheets.tsx"), "TimesheetsPage"),
});
const designKit = createRoute({
	getParentRoute: () => tenant,
	path: "/design",
	component: lazyRouteComponent(() => import("./routes/design.tsx"), "DesignKit"),
});

const helpModule = () => import("./routes/help.tsx");
const helpCentre = createRoute({
	getParentRoute: () => staff,
	path: "/help",
	component: lazyRouteComponent(helpModule, "HelpPage"),
});
const helpArticle = createRoute({
	getParentRoute: () => staff,
	path: "/help/$slug",
	component: lazyRouteComponent(helpModule, "HelpPage"),
});

const routeTree = root.addChildren([
	signIn,
	signInLink,
	newEmail,

	staff.addChildren([
		chooseAgency,
		answerSsg,
		platform,
		platformFeedback,
		account,
		helpCentre,
		helpArticle,
		tenant.addChildren([
			home,
			upload,
			candidateList,
			candidateNew,
			candidateRecord,
			candidateEdit,
			clientList,
			clientNew,
			clientRecord,
			clientEdit,
			importStart,
			importJob,
			checkQueue,
			documentSettings,
			formsPage,
			agreementsPage,
			formBuilder,
			rateCards,
			rateCard,
			reportsPage,
			wallBoard,
			teamPage,
			payrollPage,
			vacanciesPage,
			vacancyPage,
			tasksPage,
			accessLog,
			waysPage,
			bookingList,
			bookingNew,
			bookingPlan,

			bookingPage,
			designKit,
			timesheetsPage,
		]),
	]),
]);

export function makeRouter(queryClient: QueryClient) {
	return createRouter({
		routeTree,
		context: { queryClient },
		defaultPreload: "intent",
		defaultNotFoundComponent: NotFound,
		defaultErrorComponent: Crash,
	});
}

declare module "@tanstack/react-router" {
	interface Register {
		router: ReturnType<typeof makeRouter>;
	}
}
